import { floatingListboxPosition } from "./floating_listbox_position.mjs"

// The panel is at least 16rem wide (`min-w-[16rem]`). Clamping has to know
// that, or a picker on a narrow trigger is placed by its trigger's width and
// then paints past the right edge.
const MIN_WIDTH = 256
const MARGIN = 8
// A list short enough to be useless is worse than one that scrolls: below this
// the panel would show one option and a scrollbar.
const MIN_LIST_HEIGHT = 96

// Positions the panel of `SeivaWeb.Components.SearchableSelect`.
//
// The panel used to be `absolute` inside the field, which made every ancestor
// with `overflow` a lid: inside a drawer body (`overflow-y-auto`) a short
// drawer cut the option list in half. It CANNOT be portalled to
// `document.body` the way the `<select>`-wrapping `SearchableSelect` hook
// portals its own panel — this one's options are server-rendered and carry
// `phx-click`/`phx-target`, so the node has to stay inside the LiveView that
// owns it, where patches can keep re-rendering the filtered list.
//
// So it stays where it is and goes `position: fixed`: the containing block
// becomes the viewport, and no ancestor's `overflow` clips it any more.
//
// ⚠️ That only holds while NO ancestor carries a transform — a transformed
// ancestor is a containing block for `fixed` too, and its `overflow` starts
// clipping again. That is why the sheet/modal entrance animations run with
// `animation-fill-mode: backwards` instead of `both` (a `both` animation keeps
// its final `transform: translateX(0)` forever). `financial_close_visual_
// contract_test.exs` guards it.
const SearchableSelectPanel = {
  mounted() {
    this.frame = null
    this.handleViewportChange = () => this.schedule()

    window.addEventListener("resize", this.handleViewportChange)
    window.addEventListener("scroll", this.handleViewportChange, true)

    this.position()
  },

  updated() {
    // The server may have replaced the option set under an open panel (the
    // person is typing in the search box) — the panel's height changed with it.
    this.position()
  },

  destroyed() {
    window.removeEventListener("resize", this.handleViewportChange)
    window.removeEventListener("scroll", this.handleViewportChange, true)
    if (this.frame !== null) cancelAnimationFrame(this.frame)
  },

  schedule() {
    if (this.frame !== null) return

    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.position()
    })
  },

  position() {
    const trigger = document.getElementById(this.el.dataset.anchor)
    if (!trigger) return

    const list = this.el.querySelector("[role='listbox']")

    // Measure unconstrained first. Reading the height while last frame's
    // `max-height` is still applied would shrink the panel a little further on
    // every reposition, until it collapsed. Only the INLINE cap is cleared —
    // the list's `max-h-*` class stays, so `naturalHeight` is still the size
    // the panel has always opened at when there is room for it.
    this.el.style.maxHeight = ""
    if (list) list.style.maxHeight = ""

    const naturalHeight = this.el.scrollHeight
    // The RENDERED list height, not its `scrollHeight`: with more options than
    // the class cap allows, `scrollHeight` is the un-capped content and the
    // subtraction would go negative.
    const listHeight = list ? list.getBoundingClientRect().height : 0
    const chrome = Math.max(0, naturalHeight - listHeight)

    const rect = trigger.getBoundingClientRect()

    const placement = floatingListboxPosition(
      { top: rect.top, bottom: rect.bottom, left: rect.left, width: Math.max(rect.width, MIN_WIDTH) },
      { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight },
      naturalHeight,
      { margin: MARGIN, maxHeight: naturalHeight, preferredHeight: Math.min(naturalHeight, 200) }
    )

    // `placement.maxHeight` is the space AVAILABLE, which is usually more than
    // the panel wants. Clamping to `naturalHeight` keeps the picker the size it
    // has always been when there is room, and shrinks it — list scrolling
    // inside — only when there is not. Growing it would be a redesign, not a
    // fix for the clipping.
    const height = Math.min(naturalHeight, placement.maxHeight)

    this.el.style.position = "fixed"
    // `top-full mt-1` / `bottom-full mb-1` are the server's guess at placement;
    // `inset: auto` drops whichever of the two it picked before we set ours.
    this.el.style.inset = "auto"
    this.el.style.margin = "0"
    this.el.style.left = `${placement.left}px`
    this.el.style.top = `${placement.top}px`
    this.el.style.width = `${placement.width}px`
    // Neutralises `min-w-[16rem]`, which would otherwise reintroduce the
    // overflow the clamp just removed on a narrow viewport.
    this.el.style.minWidth = `${placement.width}px`
    this.el.style.maxHeight = `${height}px`

    if (list) list.style.maxHeight = `${Math.max(MIN_LIST_HEIGHT, height - chrome)}px`
  }
}

export default SearchableSelectPanel
